import React, { FC, useState } from "react";
import { Col, Input, Row, Select } from "antd";
import styles from "./smart-search.module.scss";
import { useIntl } from "react-intl";
import { useRouter } from "next/router";
import {
  CalendarOutlined,
  HomeOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  useGetPortalFacilityHierarchyQuery,
  useGetPortalUserAppointmentsQuery,
} from "../../src/generated/graphql";
import { AveliosButton } from "../common-layout/avelios-button";
import { highlightTitle } from "../../helpers/utils";
import { extractTranslation } from "../../helpers/formatters";
import moment from "moment";

const { Option, OptGroup } = Select;

interface AveliosOption {
  route: string;
  key: string;
  text: string;
  description?: React.ReactNode;
  icon?: React.ReactNode;
}

export const SmartSearch: FC = () => {
  const { formatMessage } = useIntl();

  const router = useRouter();
  const locale = router.locale;

  const [searchQuery, setSearchQuery] = useState<string>();

  const { data: appointmentsData, loading: appointmentsLoading } =
    useGetPortalUserAppointmentsQuery();

  const appointments =
    appointmentsData?.getPortalUserAppointments?.proxyAppointments;

  const previousOrganizationalUnits: AveliosOption[] | undefined = [];
  appointments
    ?.filter((appointmentProxy) =>
      moment(appointmentProxy?.appointment?.startTime?.seconds * 1000).isBefore(
        moment()
      )
    )
    ?.map((appointment) => appointment!.portalFacilityPath)
    .filter(
      (portalFacilityPath) =>
        !!portalFacilityPath?.portalFacilities &&
        portalFacilityPath.portalFacilities.length > 0
    )
    ?.map((portalFacilityPath) => {
      return portalFacilityPath!.portalFacilities![
        portalFacilityPath!.portalFacilities!.length - 1
      ];
    })
    ?.map((facility) => ({
      key: facility!.id!,
      icon: <HomeOutlined />,
      text: facility!.name!,
      description: extractTranslation(facility?.shortDescription, locale),
      route: `facility-selection?facilityId=${facility!.id!}`,
    }))
    .forEach((facility) => {
      if (
        !previousOrganizationalUnits.find(
          (entry) => entry.key === facility?.key
        )
      ) {
        previousOrganizationalUnits.push(facility!);
      }
    });

  const { data: facilitiesData, loading: facilitiesLoading } =
    useGetPortalFacilityHierarchyQuery({
      variables: { depth: 0 },
    });
  const facilityOptions: AveliosOption[] | undefined =
    facilitiesData?.getPortalFacilityHierarchy?.portalFacilities?.map(
      (facility) => ({
        key: facility!.id!,
        icon: <HomeOutlined />,
        text: facility!.name!,
        description: extractTranslation(facility?.shortDescription, locale),
        route: `facility-selection?facilityId=${facility!.id!}`,
      })
    );

  const handleOnSelect = (value: string) => {
    router.push(value);
  };

  const suggestions: AveliosOption[] = [
    {
      key: "new-appointment",
      route: "facility-selection",
      text: formatMessage({ id: "smart-search.suggestion.new-appointment" }),
      icon: <PlusOutlined />,
    },
    {
      key: "appointments",
      route: "appointments",
      text: formatMessage({ id: "smart-search.suggestion.my-appointments" }),
      icon: <CalendarOutlined />,
    },
    // {
    //   key: "documents",
    //   route: "documents",
    //   text: formatMessage({ id: "smart-search.suggestion.my-documents" }),
    //   icon: <FileOutlined />,
    // },
  ];

  const formatOption = (suggestion: AveliosOption, action?: boolean) => {
    return (
      <Option
        key={suggestion.key}
        value={suggestion.route}
        text={suggestion.text}
      >
        <Row className={action ? styles.optionAction : styles.option}>
          <Col span={24}>
            {suggestion.icon && (
              <span className={styles.optionIcon}>{suggestion.icon}</span>
            )}
            {highlightTitle(suggestion.text, searchQuery)}
          </Col>
        </Row>
        {suggestion.description && (
          <Row>
            <Col
              span={24}
              className={`${styles.optionDescription} description`}
            >
              {suggestion.description}
            </Col>
          </Row>
        )}
      </Option>
    );
  };

  const loading = appointmentsLoading || facilitiesLoading;

  return (
    <Input.Group compact>
      <Select
        loading={loading}
        dropdownMatchSelectWidth={true}
        showSearch
        bordered={false}
        allowClear
        style={{ width: "calc(100% - 200px)" }}
        suffixIcon={<SearchOutlined />}
        showArrow={false}
        searchValue={searchQuery}
        onSearch={setSearchQuery}
        autoClearSearchValue
        onSelect={handleOnSelect}
        className={styles.search}
        size={"large"}
        filterOption={(inputValue, option) =>
          option?.options?.find((option: any) =>
            option.text.toLowerCase().includes(inputValue.toLowerCase())
          )
        }
        maxLength={20}
        placeholder={formatMessage({ id: "smart-search.placeholder" })}
      >
        <OptGroup label={formatMessage({ id: "smart-search.suggestions" })}>
          {suggestions.map((suggestion) => formatOption(suggestion, true))}
        </OptGroup>
        <OptGroup label={formatMessage({ id: "smart-search.facilities" })}>
          {facilityOptions?.map((facility) => formatOption(facility))}
        </OptGroup>
        {previousOrganizationalUnits.length > 0 && (
          <OptGroup
            label={formatMessage({ id: "smart-search.prev-facilities" })}
          >
            {previousOrganizationalUnits?.map((facility) =>
              formatOption(facility)
            )}
          </OptGroup>
        )}
      </Select>
      <div
        style={{
          backgroundColor: "#FFE388",
          borderRadius: "0 10px 10px 0",
          border: "0.5px solid black",
        }}
      >
        <AveliosButton
          loading={loading}
          style={{
            backgroundColor: "#FFE388",
            borderRadius: "0 10px 10px 0",
          }}
          size={"large"}
          icon={<SearchOutlined />}
        />
      </div>
    </Input.Group>
  );
};
